import { generatePath } from 'react-router';
import { call, put, takeLatest, takeEvery, select, all, debounce } from 'redux-saga/effects';

import { navigateToIntervention } from 'actions/navigateActions';
import * as types from 'constants/ActionTypes';
import { ANY_SENDERS, ASSIGNEE_USER_GROUPS, AUTO_REFRESH, CHECK_IN_SENDERS, CHECK_OUT_SENDERS, INTERVENTION_FILTERS, SEARCH, SENDERS, STEPS } from 'constants/FilterConstants';
import { DEBOUNCE_TIME } from 'constants/Global';
import { POLLING_DEBOUNCE } from 'constants/InterventionConstants';
import { MODULES } from 'constants/ModuleConstants';
import { PATH } from 'constants/RoutesConstants';
import { CHECK_IN_STATUS, CHECK_OUT_STATUS, INCLUDE_MANUAL_CHECK_OUT } from 'constants/SelfServiceConstants';
import { CONNECTION_ESTABLISHED } from 'modules/app/actionTypes';
import { scopeToPage } from 'modules/app/sagas/utils';
import * as interventionCoreActions from 'modules/interventions/actions/core';
import * as interventionDashboardActions from 'modules/interventions/actions/dashboard';
import * as interventionFilterActions from 'modules/interventions/actions/filters';
import * as actionTypes from 'modules/interventions/actionTypes';
import {
  canFetchInterventions,
  getFormattedDashboardFilters,
  getInterventionsFilters,
  getParams,
  isReminderDashboard,
} from 'modules/interventions/selectors/dashboard';
import { save as persistState } from 'modules/localStorage/actions';
import { navigate } from 'modules/router/navigate';
import { getUserGroups } from 'modules/userGroups/actions';
import { getSelectedDealerId } from 'selectors/dealerSelectors';

const SENDER_FIELDS = [SENDERS, CHECK_IN_SENDERS, CHECK_OUT_SENDERS, ANY_SENDERS];

function* resetActiveSendersWorker() {
  const filters = yield select(getInterventionsFilters);
  const activeSenderFields = SENDER_FIELDS.filter(field => filters[field]?.length > 0);
  if (activeSenderFields.length > 0) {
    yield all(activeSenderFields.map(field => put(interventionFilterActions.setInterventionsFilter(field, []))));
  }
}

const DASHBOARD_FILTER_MODIFIER = [
  actionTypes.SET_SORT,
  actionTypes.REMOVE_INTERVENTION_FILTER,
  actionTypes.SET_SCHEDULABLE_TYPE,
  actionTypes.CLEAR_INTERVENTIONS_FILTERS,
  actionTypes.TOGGLE_QUICK_FILTER,
];

const INTERVENTIONS_DASHBOARD_PATHS = [PATH.AFTERSALES, PATH.SALES, PATH.PARTS, PATH.BODYWORKS];

function* saveLocalStorageWorker() {
  yield put(persistState());
}

function* refreshInterventionsWorker({ type, payload } = {}) {
  if (type !== actionTypes.TOGGLE_QUICK_FILTER || payload !== AUTO_REFRESH) {
    const params = yield select(state => getParams(state, new Date().toISOString()));
    const canFetch = yield select(canFetchInterventions);
    const isReminder = yield select(isReminderDashboard);

    if (canFetch) {
      yield put(interventionCoreActions.fetchInterventions({ ...params, page: 0 }, { source: isReminder ? 'reminders' : 'dashboard' }));
    }
  }
}

function* pollRefreshInterventions() {
  const { autoRefreshResults } = yield select(getInterventionsFilters);
  if (autoRefreshResults) {
    yield refreshInterventionsWorker();
  }
}

function* afterToggleAutoRefresh() {
  const { autoRefreshResults } = yield select(getInterventionsFilters);
  if (autoRefreshResults) {
    yield put(interventionDashboardActions.startFetchPolling());
  } else {
    yield put(interventionDashboardActions.stopFetchPolling());
  }
}

const AFTER_TOGGLE_HANDLER = {
  [AUTO_REFRESH]: afterToggleAutoRefresh,
};

function* afterToggleQuickFilter({ payload }) {
  if (INTERVENTION_FILTERS.includes(payload)) {
    yield call(saveLocalStorageWorker);
  }

  const handler = AFTER_TOGGLE_HANDLER[payload];
  if (handler) {
    yield call(handler);
  }
}

function* afterInitInterventionDashboardWorker() {
  const dealerId = yield select(getSelectedDealerId);
  const { autoRefreshResults } = yield select(getInterventionsFilters);

  yield put(getUserGroups(dealerId));

  if (autoRefreshResults) {
    yield put(interventionDashboardActions.startFetchPolling());
  } else {
    yield refreshInterventionsWorker();
  }
}

/**
 * @param {import('modules/dealers/types').SelectDealerAction} action
 */
function* changeSelectedDealerWorker({ payload: { dealerId } }) {
  yield all([
    put(getUserGroups(dealerId)),
    put(interventionFilterActions.setInterventionsFilter(STEPS, [])),
    put(interventionFilterActions.setInterventionsFilter(CHECK_IN_STATUS, [])),
    put(interventionFilterActions.setInterventionsFilter(CHECK_OUT_STATUS, [])),
    put(interventionFilterActions.setInterventionsFilter(ASSIGNEE_USER_GROUPS, [])),
    put(interventionFilterActions.setInterventionsFilter(INCLUDE_MANUAL_CHECK_OUT, false)),
    put(interventionFilterActions.setInterventionsFilter(CHECK_IN_SENDERS, [])),
    put(interventionFilterActions.setInterventionsFilter(CHECK_OUT_SENDERS, [])),
  ]);
}

export function* redirectToDetailsPage({ payload: { id: interventionId, module }, meta }) {
  if (!meta.skipRedirection) {
    yield call(navigateToIntervention, interventionId, module);
  }
}

function* redirectToReminderDetailsPage({ payload: data }) {
  const [intervention] = data;
  const hasMany = data.length > 1;

  if (intervention) {
    if (hasMany) {
      yield call(navigate, generatePath(MODULES[intervention.module].dashboard));
      yield put(interventionFilterActions.setInterventionsFilter(SEARCH, intervention.referenceId));
    } else {
      yield call(navigate, generatePath(MODULES[intervention.module].path, { interventionId: intervention.id }));
    }
  }
}

function* afterPatchIntervention({ meta }) {
  if (meta?.dashboardRedirect) {
    const route = MODULES[meta.dashboardRedirect].dashboard ?? PATH.AFTERSALES.path;
    yield call(navigate, generatePath(route));
  }
}

function* refreshReminderDashboard() {
  const filters = yield select(state => getFormattedDashboardFilters(state, new Date().toISOString()));
  yield put(interventionCoreActions.fetchInterventions(filters, { source: 'reminders' }));
}

// Reminders
function* onReminderDashboardPageWorker() {
  yield takeEvery([actionTypes.DELAY_REMINDER_SUCCESS, actionTypes.UPDATE_INTERVENTION_STATUS_SUCCESS], refreshReminderDashboard);
}

// Interventions dashboard cases
function* onDashboardPageWorker() {
  yield takeEvery(types.SELECT_DEALER, changeSelectedDealerWorker);
  yield takeEvery(actionTypes.INIT_DASHBOARD, afterInitInterventionDashboardWorker);

  // Interventions long polling
  yield debounce(POLLING_DEBOUNCE, actionTypes.FETCH_INTERVENTIONS_SUCCESS, pollRefreshInterventions);
  yield takeEvery([actionTypes.FETCH_INTERVENTIONS_START_POLLING, CONNECTION_ESTABLISHED], pollRefreshInterventions);
}

export default function* interventionDashboardSagas() {
  // Dashboard pages
  yield scopeToPage(INTERVENTIONS_DASHBOARD_PATHS, onDashboardPageWorker);
  yield scopeToPage([PATH.REMINDERS, PATH.PARTS_REMINDER], onReminderDashboardPageWorker);

  // Global cases
  yield debounce(DEBOUNCE_TIME, actionTypes.SET_INTERVENTIONS_FILTERS, refreshInterventionsWorker);
  yield takeLatest(DASHBOARD_FILTER_MODIFIER, refreshInterventionsWorker);
  yield takeLatest(actionTypes.SET_INTERVENTIONS_SENDER_FILTER_MODE, resetActiveSendersWorker);
  yield takeEvery(actionTypes.CREATE_INTERVENTION_SUCCESS, redirectToDetailsPage);
  yield takeEvery(actionTypes.TOGGLE_QUICK_FILTER, afterToggleQuickFilter);
  yield takeEvery(actionTypes.DELETE_INTERVENTION_SUCCESS, afterPatchIntervention);
  yield takeEvery(actionTypes.UPDATE_INTERVENTION_STATUS_SUCCESS, afterPatchIntervention);
  yield takeEvery(actionTypes.CREATE_INTERVENTION_REMINDER_SUCCESS, redirectToReminderDetailsPage);
}
